"use client";

import * as React from "react";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { Pagination, PaginationContent, PaginationItem, PaginationLink } from "@/components/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const filters = [{
  label: "ALL",
  value: "all"
}];
function ProductsCatalog() {
  const {
    products,
    loading,
    error,
    refetch,
    setSorting,
    currentPage,
    totalPages,
    setPage,
    total
  } = useEcommerceProducts();
  const [activeFilter, setActiveFilter] = React.useState("all");
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedProduct, setSelectedProduct] = React.useState<any>(null);
  const handleSort = (value: string) => {
    if (value === "newest") setSorting("date", "desc");else if (value === "oldest") setSorting("date", "asc");else if (value === "price-asc") setSorting("price", "asc");else if (value === "price-desc") setSorting("price", "desc");else if (value === "name") setSorting("title", "asc");
  };
  const filteredProducts = products.filter(p => {
    const matchesFilter = activeFilter === "all" || p.categories?.some(c => c.slug === activeFilter);
    const matchesSearch = !searchTerm || p.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });
  return <>
      {}
      <div className="border-y border-border/40">
        <div className="flex flex-col gap-4 py-5 md:flex-row md:items-center md:justify-between">
          <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
            <div className="flex items-center gap-2 md:flex-wrap">
              {filters.map((filter, i) => <Button key={filter.value} data-index={i} variant="ghost" onClick={() => setActiveFilter(filter.value)} className={`shrink-0 h-auto px-4 py-2 text-[11px] tracking-[0.2em] font-default border rounded-lg transition-colors ${activeFilter === filter.value ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90" : "border-border/60 text-foreground/70 hover:border-primary hover:text-primary hover:bg-transparent"}`}>
                  {filter.label}
                </Button>)}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Select onValueChange={handleSort} defaultValue="newest">
              <SelectTrigger className="h-9 border-border/60 bg-transparent text-[11px] tracking-[0.2em] font-default uppercase">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
                <SelectItem value="price-asc">Price: Low to High</SelectItem>
                <SelectItem value="price-desc">Price: High to Low</SelectItem>
                <SelectItem value="name">Name</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(v => !v)} aria-label="Search products" className="h-9 w-9 border border-border/60 text-foreground/80 hover:bg-primary hover:text-primary-foreground rounded-lg">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {searchOpen && <div className="pb-5">
            <Input autoFocus type="search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search our inventory..." className="border-border/60 bg-transparent font-default text-base" />
          </div>}
      </div>

      {}
      <div className="mt-12">
        {loading ? <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
            {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="aspect-square bg-muted/20 animate-pulse rounded-lg" />)}
          </div> : error ? <div className="py-16 text-center">
            <p className="text-sm text-muted-foreground font-default tracking-wider">
              UNABLE TO LOAD INVENTORY
            </p>
            <Button onClick={refetch} variant="ghost" className="mt-4 text-xs tracking-[0.2em] font-default uppercase underline">
              Retry
            </Button>
          </div> : filteredProducts.length === 0 ? <div className="py-24 text-center">
            <p className="font-default text-2xl text-foreground/70">
              No items found.
            </p>
            <p className="mt-2 text-[11px] tracking-[0.2em] font-default uppercase text-muted-foreground">
              Try a different filter
            </p>
          </div> : <div className="grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-8 lg:grid-cols-4">
            {filteredProducts.map((product, i) => <article key={product.id} data-index={i} className="group relative">
                <div className="relative overflow-hidden bg-card shadow-md rounded-lg transition-transform duration-500 group-hover:-translate-y-1">
                  <Link to={product.link}>
                    <div className="relative aspect-square overflow-hidden p-4 md:p-6">
                      {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" /> : <div className="flex h-full w-full items-center justify-center bg-muted/30">
                          <span className="font-default text-4xl text-foreground/20">
                            ITEM
                          </span>
                        </div>}
                    </div>
                  </Link>

                  {}
                  <div className="pointer-events-none absolute inset-x-4 bottom-4 translate-y-4 opacity-0 transition-all duration-300 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 md:inset-x-6 md:bottom-6">
                    <Button onClick={() => setSelectedProduct(product)} variant="default" className="w-full bg-primary text-primary-foreground text-[11px] tracking-[0.25em] font-default uppercase hover:bg-primary/90 rounded-lg">
                      Quick View
                    </Button>
                  </div>
                </div>

                <div className="mt-4 px-1">
                  {product.categories && product.categories.length > 0 && <p className="text-[10px] tracking-[0.25em] font-default uppercase text-muted-foreground">
                      {product.categories[0].name}
                    </p>}
                  <Link to={product.link}>
                    <h3 className="mt-2 font-default text-base leading-snug text-foreground line-clamp-2 transition-colors hover:text-primary md:text-lg">
                      {product.title}
                    </h3>
                  </Link>
                  <p className="mt-2 text-[11px] tracking-[0.2em] font-default uppercase text-primary">
                    {product.discounted_price ? <>
                        <span>R{product.discounted_price.toFixed(2)}</span>
                        <span className="ml-2 text-muted-foreground line-through">
                          R{product.price.toFixed(2)}
                        </span>
                      </> : <span>R{product.price?.toFixed(2)}</span>}
                  </p>
                </div>
              </article>)}
          </div>}

        {}
        {totalPages > 1 && <div className="mt-16 flex justify-center">
            <Pagination>
              <PaginationContent className="gap-3">
                {Array.from({
              length: totalPages
            }).map((_, i) => {
              const page = i + 1;
              const isActive = page === currentPage;
              return <React.Fragment key={page}>
                      <PaginationItem>
                        <PaginationLink isActive={isActive} onClick={e => {
                    e.preventDefault();
                    setPage(page);
                  }} className={`h-auto w-auto rounded-lg border-0 bg-transparent px-1 text-sm font-default tracking-wider ${isActive ? "text-primary underline underline-offset-8" : "text-foreground/50 hover:text-primary"}`}>
                          {String(page).padStart(2, "0")}
                        </PaginationLink>
                      </PaginationItem>
                      {page < totalPages && <span className="self-center text-foreground/30">·</span>}
                    </React.Fragment>;
            })}
              </PaginationContent>
            </Pagination>
          </div>}

        {total > 0 && <p className="mt-8 text-center text-[10px] tracking-[0.25em] font-default uppercase text-muted-foreground">
            Showing {filteredProducts.length} of {total} items
          </p>}
      </div>

      {}
      <Dialog open={!!selectedProduct} onOpenChange={open => !open && setSelectedProduct(null)}>
        <DialogContent showCloseButton={false} className="max-w-4xl border-border/40 bg-background p-0 rounded-lg">
          {selectedProduct && <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative aspect-square bg-card p-6 md:p-10">
                {selectedProduct.featured_image_url ? <img src={selectedProduct.featured_image_url} alt={selectedProduct.title} loading="lazy" className="h-full w-full object-cover rounded-lg" /> : <div className="flex h-full w-full items-center justify-center bg-muted/30 rounded-lg">
                    <span className="font-default text-6xl text-foreground/20">
                      ITEM
                    </span>
                  </div>}
              </div>

              <div className="flex flex-col p-6 md:p-10">
                <div className="flex items-start justify-between">
                  {selectedProduct.categories && selectedProduct.categories.length > 0 ? <p className="text-[10px] tracking-[0.3em] font-default uppercase text-muted-foreground">
                      {selectedProduct.categories[0].name}
                    </p> : <span />}
                  <Button variant="ghost" size="icon" onClick={() => setSelectedProduct(null)} className="h-8 w-8 text-foreground/60 hover:text-foreground hover:bg-transparent rounded-lg" aria-label="Close">
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <DialogHeader className="mt-4">
                  <DialogTitle className="font-default text-2xl leading-tight text-foreground md:text-3xl">
                    {selectedProduct.title}
                  </DialogTitle>
                </DialogHeader>

                <p className="mt-4 text-sm tracking-[0.2em] font-default uppercase text-primary">
                  {selectedProduct.discounted_price ? <>
                      <span>R{selectedProduct.discounted_price.toFixed(2)}</span>
                      <span className="ml-2 text-muted-foreground line-through">
                        R{selectedProduct.price.toFixed(2)}
                      </span>
                    </> : <span>R{selectedProduct.price?.toFixed(2)}</span>}
                </p>

                {selectedProduct.short_description && <div className="mt-6 font-default text-sm leading-relaxed text-foreground/80 [&_p]:mb-2" dangerouslySetInnerHTML={{
              __html: selectedProduct.short_description
            }} />}

                <div className="mt-6 space-y-3 border-t border-border/40 pt-6">
                  <div className="flex justify-between text-[11px] tracking-[0.2em] font-default uppercase">
                    <span className="text-muted-foreground">Material</span>
                    <span className="text-foreground">
                      {selectedProduct.customFields?.material || "Premium Grade"}
                    </span>
                  </div>
                  <div className="flex justify-between text-[11px] tracking-[0.2em] font-default uppercase">
                    <span className="text-muted-foreground">Dimensions</span>
                    <span className="text-foreground">
                      {selectedProduct.customFields?.dimensions || "Standard"}
                    </span>
                  </div>
                  <div className="flex justify-between text-[11px] tracking-[0.2em] font-default uppercase">
                    <span className="text-muted-foreground">Origin</span>
                    <span className="text-foreground">Global Standard</span>
                  </div>
                </div>

                <div className="mt-auto pt-8">
                  <AddToCartButton productId={selectedProduct.id} className="w-full" buttonClassName="bg-primary text-primary-foreground w-full rounded-lg h-12 text-[11px] tracking-[0.3em] font-default uppercase" text="Add to Cart" showQuantity={true} />
                  <Link to={selectedProduct.link} className="mt-4 block text-center text-[11px] tracking-[0.25em] font-default uppercase text-foreground/60 underline underline-offset-4 hover:text-primary">
                    View Full Details
                  </Link>
                </div>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </>;
}
export default function Catalog() {
  const wp_query = new WP_Query({
    per_page: 12,
    order: "desc",
    orderby: "date"
  });
  return;
}